// Learn Section Utilities

/**
 * Extracts video ID and platform from a video URL
 */
export const extractVideoId = (url) => {
  if (!url) return null;

  // Clean and normalize the URL
  const cleanUrl = url.trim();

  // YouTube patterns - multiple reliable approaches
  const youtubePatterns = [
    // Standard watch URL: https://www.youtube.com/watch?v=VIDEO_ID
    /[?&]v=([^"&?/]{11})/,
    // Short URL: https://youtu.be/VIDEO_ID
    /youtu\.be\/([^"&?/]{11})/,
    // Embed URL: https://www.youtube.com/embed/VIDEO_ID
    /youtube\.com\/embed\/([^"&?/]{11})/,
    // Any 11-character sequence that looks like a YouTube ID in YouTube URLs
    /youtube\.com\/.*\/([^"&?/]{11})(?:[?&]|$)/
  ];

  for (const pattern of youtubePatterns) {
    const match = cleanUrl.match(pattern);
    if (match && match[1]) {
      return { platform: 'youtube', id: match[1] };
    }
  }

  // Vimeo patterns
  const vimeoPatterns = [
    // Standard Vimeo URL: https://vimeo.com/VIDEO_ID
    /vimeo\.com\/(\d+)/,
    // Vimeo player URL: https://player.vimeo.com/video/VIDEO_ID
    /player\.vimeo\.com\/video\/(\d+)/
  ];

  for (const pattern of vimeoPatterns) {
    const match = cleanUrl.match(pattern);
    if (match && match[1]) {
      return { platform: 'vimeo', id: match[1] };
    }
  }

  return null;
};

/**
 * Generates an embed URL for a video
 */
export const getEmbedUrl = (videoInfo) => {
  if (!videoInfo) return null;

  switch (videoInfo.platform) {
    case 'youtube':
      return `https://www.youtube.com/embed/${videoInfo.id}`;
    case 'vimeo':
      return `https://player.vimeo.com/video/${videoInfo.id}`;
    default:
      return null;
  }
};

/**
 * Generates a thumbnail URL for a video
 */
export const getThumbnailUrl = (videoUrl, platform) => {
  if (!videoUrl) return null;
  
  const videoInfo = extractVideoId(videoUrl);
  if (!videoInfo) return null;

  // If platform is specified, ensure it matches
  if (platform && videoInfo.platform !== platform) {
    // If it doesn't match, still try to return a thumbnail based on the detected platform
    // but log a warning
    console.warn(`Platform mismatch: expected ${platform}, detected ${videoInfo.platform} for URL ${videoUrl}`);
  }

  switch (videoInfo.platform) {
    case 'youtube':
      // Use hqdefault as it's more reliable than maxresdefault
      return `https://img.youtube.com/vi/${videoInfo.id}/hqdefault.jpg`;
    case 'vimeo':
      return `https://vumbnail.com/${videoInfo.id}.jpg`;
    default:
      return null;
  }
};
